import {getI18n} from 'react-i18next';

import type {InfinityClient} from '@pexip/infinity';
import {buildUrl} from '@pexip/router';

import {logger} from '../logger';
import {push, redirectInMeeting, router, toMeetingEntryRoute} from '../router';

import {
    applyMeetingLinkFromLocation,
    applyMeetingLinkParams,
    parseMeetingLinkUrl,
} from './applyMeetingLinkParams';
import type {MeetingLinkParams} from './parseMeetingLinkParams';

/**
 * Creates the functions that open meeting links, such as
 * `/m/<alias>?name=<name>&pin=<pin>`: the link the page was loaded at, and
 * links opened while the application runs.
 *
 * Both open a link the same way: a bare meeting route is sent to its express
 * flow and the link settings are applied. A link opened later is parsed first
 * and pushed as a new route only when it is well formed. Create them once
 * branding is applied, so that link settings override branding defaults.
 *
 * @param infinityClient - Client receiving the call credentials of a link.
 * @returns The page-load and later-link openers.
 *
 * @example
 * await bootstrapBranding();
 * const meetingLinks = createMeetingLinks(infinity.getClient());
 * meetingLinks.openPageLoadLink();
 * meetingLinks.openLink('/m/meet.alice?name=Bob');
 */
export const createMeetingLinks = (infinityClient: InfinityClient) => ({
    /**
     * Opens the link in the location the page was loaded at. A malformed
     * link, such as a path with invalid percent-encoding, is logged and left
     * unapplied, so startup continues. The meeting route still fails to
     * decode such a path when it renders.
     */
    openPageLoadLink: () => {
        try {
            redirectInMeeting();
            applyMeetingLinkFromLocation(infinityClient);
        } catch (error: unknown) {
            logger.error({error}, 'Failed to open a meeting link');
        }
    },
    /**
     * Opens an application route as a page load at that route would.
     *
     * @param route - Route relative to the application base.
     * @returns Whether the link was opened; routes to another origin and
     * malformed links are refused and leave the route unchanged.
     */
    openLink: (route: string): boolean => {
        const url = buildUrl(route, {base: router.baseUri});
        if (!url.isInternal()) {
            return false;
        }
        const entryRoute = toMeetingEntryRoute(url);
        const entryUrl = entryRoute
            ? buildUrl(entryRoute, {base: router.baseUri})
            : url;
        let params: MeetingLinkParams;
        try {
            params = parseMeetingLinkUrl(entryUrl);
        } catch (error: unknown) {
            logger.error({error}, 'Failed to open a meeting link');
            return false;
        }
        push(entryUrl);
        applyMeetingLinkParams(infinityClient, params);
        // A page load selects the language when i18next initializes, which
        // has already happened. Branding binds react-i18next to its own
        // instance, so the default instance is not the UI's.
        if (params.language) {
            void getI18n().changeLanguage(params.language);
        }
        return true;
    },
});
